import PropTypes from "prop-types";
import { useMemo } from "react";
import NumberFormat from "react-number-format";

import { capitalize } from "lodash";
import { Pencil, Trash } from "lucide-react";
import { Input, DefaultTable, Badge, ButtonIcon } from "twyne-new.ui";

import { DefaultDragFormatter, DndSortableList, useSortableDndListItem } from "@/components/SortableDnd";
import InfoTip from "@Components/InfoTip";
import TextTruncated from "@Components/TextTruncated";

import { DroppableArea } from "./Draggable";
import Droppable from "./Droppable";
import { PageDetails, TYPE_ALAIASES } from "./utils";
import { TruncateWithTooltipFormatter } from "@/components/Grid/components/Table/formatters";

const METRICS_FORMATTERS = {
  rpu: value => <NumberFormat
    value={value || 0}
    displayType="text"
    thousandSeparator=","
    decimalSeparator="."
    prefix={'$'}
    decimalScale={2}
    fixedDecimalScale={true}
  />,
  revenue: value => <NumberFormat
    value={value || 0}
    displayType="text"
    thousandSeparator=","
    decimalSeparator="."
    prefix={'$'}
    decimalScale={2}
    fixedDecimalScale={true}
  />,
  completed: value => value || 0,
}

function SortableTableRow({ children, row, className, onMove, slotIndex }) {
  const dnd = useSortableDndListItem(row.id)

  return <>
    <DroppableArea origin="active" onMove={onMove} node={{ index: row.index, slotIndex }} type="page-item" />
    <tr
      ref={dnd.setNodeRef}
      style={dnd.style}
      className={`${className ?? ""} ${row.active ? "" : "twy-bg-base"}`}
    >
      {children}
    </tr>
  </>
}

export default function SortablePages({ items, onClick, onMove, onRemove, onDropLast, slotIndex, onChangeOptimize, optimize, totalSlotImpression, onReorder }) {
  const columns = useMemo(() => {
    const cols = [
      {
        dataField: "",
        formatter: (_, row) => <DefaultDragFormatter row={row} />,
        text: "",
        className: 'w-10 pl-4',
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "name",
        formatter: nameFormatter,
        text: "Name",
        headerClassName: "text-gray-800! dark:text-gray-700!",
        className: 'twy-text-base-accent',
      }, {
        dataField: "",
        formatter: (_, row) => experimentFormatter({ ...row, optimize, totalSlotImpression }),
        text: "Experiment",
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "type",
        formatter: typeFormatter,
        text: "Type",
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "",
        formatter: (_, row) => weightFormatter({ ...row, optimize, pages: items, onChangeOptimize }),
        text: <div className="flex items-center gap-1">
          Weight
          <InfoTip id="weight-tip">
            You can set the weight of the page to determine the percentage probability of the page being selected.
          </InfoTip>
        </div>,
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "active",
        formatter: activeFormatter,
        text: "Active",
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "rules",
        formatter: rulesFormatter,
        text: "Rules",
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "dynamicNavigation",
        formatter: dynamicFormatter,
        text: "Dynamic",
        headerClassName: "text-gray-800! dark:text-gray-700!",
      }, {
        dataField: "",
        formatter: (_, row) => actionFormatter({ page: row, id: row?.id, slotIndex, onRemove, onClick }),
        text: "",
        className: "w-8",
        headerClassName: "text-gray-800! dark:text-gray-700!",
      },
    ];
    if (!optimize.experiment) {
      cols.splice(2, 1);
    }
    return cols;
  }, [optimize, items, totalSlotImpression, onChangeOptimize, slotIndex, onRemove, onClick]);

  return <DndSortableList items={items} onSortEnd={onReorder}>
    <DefaultTable
      columns={columns}
      data={items}
      className="border twy-border-base pr-2 pb-2"
      rowWrapper={props => <SortableTableRow {...props} onMove={onMove} slotIndex={slotIndex} />}
      keyField="id"
      footer={<Droppable type="page-item" onMove={onDropLast} node={{ slotIndex }} />}
      sticky={false}
    />
  </DndSortableList>
}

const nameFormatter = (_, { id, name }) => <TruncateWithTooltipFormatter value={`#${id} - ${name}`} />

const experimentFormatter = ({ optimize, impressions, totalSlotImpression, ...props }) => {
  const metric = props[optimize.winnerMetric]
  const percent = Math.round(impressions / totalSlotImpression * 100);
  const metricFormatter = METRICS_FORMATTERS[optimize.winnerMetric];

  return <small>
    {`${capitalize(optimize.winnerMetric)}: `} {metricFormatter(metric)}
    <br />
    Impressions: {impressions || 0} ({percent || 0}%)
  </small>
}

const typeFormatter = (_, row) => <div className='flex flex-col gap-1'>
  <span> {TYPE_ALAIASES[row.type].label} </span>
  <PageDetails data={row} />
</div>

const weightFormatter = ({ pages, optimize, id, onChangeOptimize }) => {
  const weight = optimize.pagesWeight && optimize.pagesWeight[id] || 1

  const handleChangeWeight = e => {
    onChangeOptimize({
      ...optimize,
      pagesWeight: {
        ...optimize?.pagesWeight,
        [id]: Number(e.target.value),
      },
    })
  }

  if (pages.length === 1) {
    return 'N/A'
  }

  const totalWeight = pages.reduce((acc, page) => acc + (optimize.pagesWeight && optimize.pagesWeight[page.id] || 1), 0)

  return <>
    <Input
      type="number"
      value={weight}
      onChange={handleChangeWeight}
      onClick={e => e.stopPropagation()}
      className='w-[50px] p-0 pl-1'
    />
    <small style={{ color: "#b3b8bb" }}>
      {((weight / totalWeight) * 100).toFixed()}%
    </small>
  </>
}

const activeFormatter = active => <Badge variant='outline' color={`${active ? 'accent' : 'neutral'}`}>
  {active ? 'yes' : 'no'}
</Badge>

const rulesFormatter = (_, { path_pages, rules }) => <Badge variant='outline' color={`${rules || path_pages?.rules ? 'accent' : 'neutral'}`}>
  {rules || path_pages?.rules ? 'yes' : 'no'}
</Badge>

const dynamicFormatter = (_, { dynamicNavigation }) => <Badge variant='outline' color={`${dynamicNavigation ? 'accent' : 'neutral'}`}>
  {dynamicNavigation ? 'yes' : 'no'}
</Badge>

const actionFormatter = ({ page, id, slotIndex, onRemove, onClick }) => <div className='flex items-center gap-1'>
  <ButtonIcon content='Edit page'>
    <Pencil size={16} onClick={() => onClick(page)} />
  </ButtonIcon>
  <ButtonIcon content='Delete page'>
    <Trash size={16} onClick={evt => {
      evt.stopPropagation()
      onRemove({ pageId: id, slotIndex })
    }} />
  </ButtonIcon>
</div>

SortablePages.propTypes = {
  items: PropTypes.array,
  onDropLast: PropTypes.func,
  onClick: PropTypes.func,
  onMove: PropTypes.func,
  onRemove: PropTypes.func,
}
